import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Gift, Megaphone } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatErr, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatTile({ label, value, hint, id }) {
  return (
    <div className="rounded-md border border-[#2A2E37] bg-[#121418] p-5" data-testid={id}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">{label}</p>
      <p className="mt-3 text-2xl font-semibold tabular-nums tracking-tight" data-testid={`${id}-value`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function Panel({ title, children, id }) {
  return (
    <section className="rounded-md border border-[#2A2E37] bg-[#121418]" data-testid={id}>
      <h2 className="border-b border-[#2A2E37] px-5 py-3.5 text-sm font-semibold tracking-tight">{title}</h2>
      <div className="divide-y divide-[#2A2E37]">{children}</div>
    </section>
  );
}

const SERIES = [
  { key: "subscriptions", label: "Subscriptions", color: "#FF2E4D" },
  { key: "tips", label: "Tips", color: "#F3F4F6" },
  { key: "unlocks", label: "Message unlocks", color: "#6B7280" },
];
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString(undefined, { month: "short" });

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + (p.value || 0), 0);
  return (
    <div className="rounded-sm border border-[#2A2E37] bg-[#090A0C] px-3 py-2 text-xs shadow-none">
      <p className="mb-1 font-semibold">{monthLabel(label)} · {money(total)}</p>
      {payload.map((p) => <p key={p.dataKey} className="flex justify-between gap-6 text-gray-400"><span>{SERIES.find((x) => x.key === p.dataKey)?.label}</span><span className="tabular-nums text-white">{money(p.value)}</span></p>)}
    </div>
  );
}

function EarningsChart({ data }) {
  const total = data.reduce((a, m) => a + m.subscriptions + m.tips + m.unlocks, 0);
  return (
    <section className="mt-6 rounded-md border border-[#2A2E37] bg-[#121418] p-5" data-testid="earnings-chart">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">Earnings by month</h2>
          <p className="mt-0.5 text-xs text-gray-500" data-testid="earnings-chart-total">{money(total)} over the last 6 months</p>
        </div>
        <div className="flex gap-4">
          {SERIES.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <span className="h-2 w-2 rounded-[1px]" style={{ background: s.color }} />{s.label}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-5 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke="#2A2E37" strokeDasharray="0" />
            <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => `$${v}`} tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
            <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} position={{ y: 8 }} allowEscapeViewBox={{ x: false, y: true }} wrapperStyle={{ zIndex: 10, marginLeft: 12 }} />
            {SERIES.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} stackId="e" fill={s.color} radius={i === SERIES.length - 1 ? [2, 2, 0, 0] : 0} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

const Empty = ({ text }) => <p className="px-5 py-8 text-center text-sm text-gray-500">{text}</p>;

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/creator/dashboard").then((r) => setData(r.data)).catch((e) => setError(formatErr(e))); }, []);

  if (error) return (
    <div className="p-16 text-center text-sm text-gray-400" data-testid="dashboard-error">
      {error} <Link to={`/u/${user?.username}`} className="text-[#FF2E4D] hover:underline">Open your profile</Link>
    </div>
  );
  if (!data) return <div className="p-16 text-center text-sm text-gray-500">Loading...</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-8 md:py-14" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF2E4D]">Creator dashboard</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">Your earnings</h1>
          <p className="mt-1 text-sm text-gray-400">Membership price {money(data.price)} / month</p>
        </div>
        <div className="flex gap-2">
          <Link to="/broadcast" data-testid="dashboard-broadcast-link"
            className="btn-neon flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white">
            <Megaphone className="h-3.5 w-3.5" /> Mass message
          </Link>
          <Link to={`/u/${user.username}`} data-testid="dashboard-view-page-link"
            className="flex items-center gap-1.5 rounded-sm border border-[#2A2E37] bg-white/5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/10">
            View my page <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile id="stat-active-subscribers" label="Subscribers" value={data.subscribers_count} hint={`${data.renewing_count} renewing`} />
        <StatTile id="stat-monthly-income" label="Monthly income" value={money(data.monthly_income)} hint="From renewing members" />
        <StatTile id="stat-this-month" label="Earned this month" value={money(data.earned_this_month)} hint="Subscriptions, tips + unlocks" />
        <StatTile id="stat-lifetime" label="Lifetime" value={money(data.lifetime_earnings)} hint={`${money(data.tips_total)} from tips`} />
      </div>
      <EarningsChart data={data.monthly} />
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <Panel title="Newest subscribers" id="newest-subscribers">
          {data.newest_subscribers.length === 0 && <Empty text="No subscribers yet. Share your page to get your first." />}
          {data.newest_subscribers.map((s) => (
            <div key={s.user.id} className="flex items-center gap-3 px-5 py-3" data-testid="subscriber-row">
              <Avatar user={s.user} size="h-8 w-8" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.user.name}</p>
                <p className="text-xs text-gray-500">@{s.user.username} · joined {timeAgo(s.since)}</p>
              </div>
              <span className={`rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${s.status === "active" ? "bg-white/5 text-gray-300" : "bg-[#FF2E4D]/10 text-[#FF2E4D]"}`}>
                {s.status === "active" ? "Renewing" : "Ending"}
              </span>
              <span className="w-16 text-right text-sm tabular-nums text-gray-300">{money(s.amount)}</span>
            </div>
          ))}
        </Panel>
        <Panel title="Recent tips" id="recent-tips">
          {data.recent_tips.length === 0 && <Empty text="No tips yet." />}
          {data.recent_tips.map((t, i) => (
            <div key={i} className="flex gap-3 px-5 py-3" data-testid="tip-row">
              <Avatar user={t.user} size="h-8 w-8" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center justify-between text-sm">
                  <span className="truncate font-medium">@{t.user.username}</span>
                  <span className="flex items-center gap-1 tabular-nums text-white"><Gift className="h-3.5 w-3.5 text-[#FF2E4D]" />{money(t.amount)}</span>
                </p>
                {t.message && <p className="mt-0.5 line-clamp-2 text-xs text-gray-400">{t.message}</p>}
                <p className="mt-0.5 text-[11px] text-gray-600">{timeAgo(t.created_at)}</p>
              </div>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}
